/* GIST LIBRARY — Focus Sessions page (gxf-*)
   Picker (tabs) · day rail · card tilt · anatomy sync · .gx-ap a11y layer.
   Dependency-free. Idempotent via data-gxf-init + one-shot load guard.
   Re-inits on shopify:section:load. Motion gated on reduced-motion / fine pointer. */
(function () {
  'use strict';
  if (window.__gxfLoaded) return;
  window.__gxfLoaded = true;

  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mqFine = window.matchMedia('(hover: hover) and (pointer: fine)');
  var uidCounter = 0;

  function once(el) {
    if (el.hasAttribute('data-gxf-init')) return false;
    el.setAttribute('data-gxf-init', '1');
    return true;
  }

  /* ---------- §2 State picker (ARIA tabs; no-JS shows all panels) ---------- */
  function initPicker(root) {
    (root || document).querySelectorAll('.gxf-picker__inner').forEach(function (box) {
      if (!once(box)) return;
      var wrap = box.querySelector('.gxf-picker__pills');
      var pills = Array.prototype.slice.call(box.querySelectorAll('.gxf-pill'));
      var panels = Array.prototype.slice.call(box.querySelectorAll('.gxf-panel'));
      /* Misconfigured section (counts diverge) -> leave the no-JS view */
      if (!wrap || !pills.length || pills.length !== panels.length) return;

      var uid = 'gxf' + (++uidCounter);
      box.classList.add('gxf-js');
      wrap.setAttribute('role', 'tablist');
      pills.forEach(function (pill, i) {
        pill.id = uid + '-tab-' + i;
        pill.setAttribute('role', 'tab');
        pill.setAttribute('aria-controls', uid + '-panel-' + i);
      });
      panels.forEach(function (pn, i) {
        pn.id = uid + '-panel-' + i;
        pn.setAttribute('role', 'tabpanel');
        pn.setAttribute('aria-labelledby', uid + '-tab-' + i);
      });

      function select(idx, focus) {
        pills.forEach(function (p, i) {
          var on = i === idx;
          p.setAttribute('aria-selected', on ? 'true' : 'false');
          p.tabIndex = on ? 0 : -1;
        });
        panels.forEach(function (pn, i) {
          pn.classList.toggle('is-active', i === idx);
        });
        if (focus) pills[idx].focus();
      }

      pills.forEach(function (pill, i) {
        pill.addEventListener('click', function () { select(i, false); });
        pill.addEventListener('keydown', function (ev) {
          var to = null;
          if (ev.key === 'ArrowRight' || ev.key === 'ArrowDown') to = (i + 1) % pills.length;
          if (ev.key === 'ArrowLeft' || ev.key === 'ArrowUp') to = (i - 1 + pills.length) % pills.length;
          if (ev.key === 'Home') to = 0;
          if (ev.key === 'End') to = pills.length - 1;
          if (to === null) return;
          ev.preventDefault();
          select(to, true);
        });
      });
      select(0, false);
    });
  }

  /* ---------- §3 Day rail: draw on reveal + scroll-spy stops ---------- */
  function initRail(root) {
    (root || document).querySelectorAll('.gxf-day').forEach(function (day) {
      if (!once(day)) return;
      var rail = day.querySelector('.gxf-day__rail');
      if (rail && 'IntersectionObserver' in window && !mqReduce.matches) {
        var draw = new IntersectionObserver(function (entries, obs) {
          entries.forEach(function (e) {
            if (e.isIntersecting) { rail.classList.add('is-drawn'); obs.disconnect(); }
          });
        }, { threshold: 0.4 });
        draw.observe(rail);
      } else if (rail) {
        rail.classList.add('is-drawn');
      }

      var stops = day.querySelectorAll('.gxf-stop');
      var dots = day.querySelectorAll('.gxf-rail-stop');
      if (stops.length && dots.length && 'IntersectionObserver' in window) {
        var spy = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            var i = Number(e.target.getAttribute('data-gxf-index'));
            if (dots[i]) dots[i].classList.toggle('is-now', e.isIntersecting);
          });
        }, { threshold: 0.6 });
        stops.forEach(function (s) { spy.observe(s); });
      }
    });
  }

  /* ---------- §3 Card tilt (<=4deg, fine pointer + motion OK only) ---------- */
  function initTilt(root) {
    if (mqReduce.matches || !mqFine.matches) return;
    (root || document).querySelectorAll('.gxf-card').forEach(function (card) {
      if (card.hasAttribute('data-gxf-tilt')) return;
      card.setAttribute('data-gxf-tilt', '1');
      card.addEventListener('mousemove', function (ev) {
        var r = card.getBoundingClientRect();
        var x = (ev.clientX - r.left) / r.width - 0.5;
        var y = (ev.clientY - r.top) / r.height - 0.5;
        card.style.setProperty('--gxf-ry', (x * 4).toFixed(2) + 'deg');
        card.style.setProperty('--gxf-rx', (y * -4).toFixed(2) + 'deg');
      });
      card.addEventListener('mouseleave', function () {
        card.style.setProperty('--gxf-ry', '0deg');
        card.style.setProperty('--gxf-rx', '0deg');
      });
    });
  }

  /* ---------- §4 Anatomy: band reveal (JS-gated so no-JS stays visible) ---------- */
  function initAnatomy(root) {
    (root || document).querySelectorAll('.gxf-anatomy').forEach(function (sec) {
      if (!once(sec)) return;
      var chart = sec.querySelector('.gxf-chart');
      if (chart && 'IntersectionObserver' in window && !mqReduce.matches) {
        chart.classList.add('gxf-anim');
        var io = new IntersectionObserver(function (entries, obs) {
          entries.forEach(function (e) {
            if (e.isIntersecting) { chart.classList.add('is-in'); obs.disconnect(); }
          });
        }, { threshold: 0.35 });
        io.observe(chart);
      }

      var phases = sec.querySelectorAll('.gxf-phase');
      var markers = sec.querySelectorAll('.gxf-marker');
      phases.forEach(function (ph, i) {
        function hot(on) {
          ph.classList.toggle('is-hot', on);
          if (markers[i]) markers[i].classList.toggle('is-hot', on);
        }
        ph.addEventListener('mouseenter', function () { hot(true); });
        ph.addEventListener('mouseleave', function () { hot(false); });
        ph.addEventListener('focusin', function () { hot(true); });
        ph.addEventListener('focusout', function () { hot(false); });
      });
    });
  }

  /* ---------- §1 Progressive a11y over the shared .gx-ap player ----------
     gist-premium-pages.js owns playback; we only mirror state to ARIA. */
  function initApA11y(root) {
    (root || document).querySelectorAll('.gx-ap').forEach(function (ap) {
      if (ap.hasAttribute('data-gxf-a11y')) return;
      ap.setAttribute('data-gxf-a11y', '1');
      var btn = ap.querySelector('.gx-ap__btn');
      var wave = ap.querySelector('.gx-ap__wave');
      if (btn && 'MutationObserver' in window) {
        btn.setAttribute('aria-pressed', 'false');
        new MutationObserver(function () {
          btn.setAttribute('aria-pressed', ap.classList.contains('is-playing') ? 'true' : 'false');
        }).observe(ap, { attributes: true, attributeFilter: ['class'] });
      }
      if (wave) {
        wave.removeAttribute('aria-hidden');
        wave.setAttribute('tabindex', '0');
        wave.setAttribute('role', 'slider');
        wave.setAttribute('aria-label', 'Seek within the sample');
        wave.setAttribute('aria-valuemin', '0');
        wave.setAttribute('aria-valuemax', '100');

        function syncVal() {
          var on = wave.querySelectorAll('i.on').length;
          var total = wave.querySelectorAll('i').length || 28;
          var pct = Math.round((on / total) * 100);
          wave.setAttribute('aria-valuenow', String(pct));
          wave.setAttribute('aria-valuetext', pct + '% of the sample');
        }
        syncVal();
        if ('MutationObserver' in window) {
          new MutationObserver(syncVal).observe(wave, {
            subtree: true, attributes: true, attributeFilter: ['class'], childList: true
          });
        }
        wave.addEventListener('keydown', function (ev) {
          var step = 0;
          if (ev.key === 'ArrowRight') step = 0.1;
          if (ev.key === 'ArrowLeft') step = -0.1;
          if (!step) return;
          ev.preventDefault();
          var r = wave.getBoundingClientRect();
          var on = wave.querySelectorAll('i.on').length;
          var total = wave.querySelectorAll('i').length || 28;
          var p = Math.min(1, Math.max(0, on / total + step));
          wave.dispatchEvent(new MouseEvent('click', {
            bubbles: true,
            clientX: r.left + p * r.width
          }));
          syncVal();
        });
      }
    });
  }

  function initAll(root) {
    initPicker(root);
    initRail(root);
    initTilt(root);
    initAnatomy(root);
    initApA11y(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
